import { Autocomplete, FormControl, Stack, TextField } from "@mui/material";
import useCountries from "../../../hooks/useCountries";
import useQueryState from "../../../hooks/useQueryState";
import { Feature } from "../../../types/geojson";
import { Dispatch, useEffect, useRef } from "react";
import { useMatch } from "react-router-dom";

const SelectGeoUnit = ({
  open,
  setOpen,
  hoveredArea,
  setHoveredArea,
}: {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  hoveredArea: string;
  setHoveredArea: Dispatch<React.SetStateAction<string>>;
}) => {
  const isDroughtRiskStep = useMatch("/dashboard/drought-risk");
  const isFinancialIndicesStep = useMatch("/dashboard/financial-indices");
  const {
    queryState: {
      country,
      aggregationLevel,
      geographicalUnit,
      payout: { comparisonFocus },
    },
    setQueryState,
  } = useQueryState();
  const {
    getCountryAdministrativeUnit: { data: geoUnit, isLoading },
  } = useCountries({ country, aggregationLevel });

  const handleGeoUnit = (_event: any, newValue: Feature | null) => {
    setQueryState({
      geographicalUnit: newValue?.properties?.location_id ?? "",
    });
  };

  useEffect(() => {
    if (
      country &&
      aggregationLevel &&
      geoUnit &&
      geoUnit?.features?.length > 0
    ) {
      setQueryState({
        geographicalUnit: geoUnit.features[0].properties?.location_id,
      });
    }
  }, [country, aggregationLevel, geoUnit]);

  const hoverTimeoutRef = useRef<number | null>(null);

  const debouncedHover = (id: string) => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredArea(id);
    }, 100);
  };

  const resetHover = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredArea("");
  };

  return (
    <Stack>
      <FormControl fullWidth>
        <Autocomplete<Feature>
          clearIcon={null}
          open={open}
          ListboxProps={
            isDroughtRiskStep
              ? {
                  onMouseLeave: resetHover,
                }
              : undefined
          }
          onOpen={() => setOpen(true)}
          onClose={(e: any, reason) => {
            if (!e.relatedTarget) setOpen(false);
            else {
              setOpen(
                isDroughtRiskStep || isFinancialIndicesStep
                  ? reason === "blur" || reason === "escape"
                  : false,
              );
            }
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              onMouseEnter={
                isDroughtRiskStep
                  ? () => debouncedHover(option.properties?.location_id ?? "")
                  : undefined
              }
            >
              {option.properties?.geounit ?? option.properties?.location_id}
            </li>
          )}
          options={
            geoUnit?.features?.sort((a, b) =>
              (a.properties?.geounit ?? "").localeCompare(
                b.properties?.geounit ?? "",
              ),
            ) ?? []
          }
          loading={isLoading}
          getOptionLabel={(option) =>
            comparisonFocus === 1
              ? ""
              : (option.properties?.geounit ??
                option.properties?.location_id ??
                "")
          }
          isOptionEqualToValue={(option, value) =>
            option.properties?.location_id === value.properties?.location_id
          }
          value={
            geoUnit?.features?.find(
              (unit) => unit.properties?.location_id === geographicalUnit,
            ) ?? null
          }
          disabled={
            !Boolean(aggregationLevel && country) ||
            Boolean(comparisonFocus === 1 && isFinancialIndicesStep)
          }
          onChange={handleGeoUnit}
          // onOpen={() => setOpen(true)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Geographical Units"
              placeholder="Geographical Units"
              sx={{
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  backgroundColor: "#f2f2f27d",
                },
                "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
                  backgroundColor: "#f2f2f27d",
                  borderColor: "#c4c4c4",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  color: "#9e9e9e",
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#9e9e9e",
                },
                "& .MuiSvgIcon-root.Mui-disabled": {
                  color: "#9e9e9e",
                },
              }}
            />
          )}
        />
      </FormControl>
    </Stack>
  );
};

export default SelectGeoUnit;
